import type React from 'react';
import { array, object, string, type AnySchema } from 'yup';
import { BaseReportElement } from '../BaseReportElement';
import { combineRenderableContent, renderDefinitionListWithAddElementSlot } from '../renderHelpers';
import { appendStructurePath } from '../structurePath';
import type {
    BuildStructureSettingsYupSchemaHelpers,
    ContextForKind,
    ElementDefinition,
    ElementContentRenderResult,
    ElementSettings,
    RenderContextKind,
    RenderRuntime,
} from '../types';

interface RowDefinition {
    children: ElementDefinition[];
    height?: string;
}

interface RowsSettings extends ElementSettings {
    rows: RowDefinition[];
}

export class RowsElement extends BaseReportElement<RenderContextKind, RowsSettings> {
    static readonly elementType = 'rows';

    static readonly layout = {
        flow: 'derived',
        getChildDefinitionLists: (settings: ElementSettings) =>
            (settings as RowsSettings).rows.map((row) => row.children),
    } as const;

    static readonly compatibleContextKinds = ['project', 'measuring-point'] as const;

    static get insertion() {
        return {
            label: gettext('ELEMENT_ROWS_LABEL'),
            supportedSlots: ['child-list'] as const,
            createDefinition: () => ({
                type: 'rows',
                settings: { rows: [{ children: [] }, { children: [] }] },
            }),
        };
    }

    static buildStructureSettingsYupSchema({
        buildRequiredDefinitionsSchema,
    }: BuildStructureSettingsYupSchemaHelpers): AnySchema {
        return object({
            rows: array()
                .required()
                .of(
                    object({
                        height: string().notRequired(),
                        children: buildRequiredDefinitionsSchema(),
                    })
                        .required()
                        .noUnknown()
                ),
        })
            .required()
            .noUnknown();
    }

    protected toCompatibleReactNode(
        ctx: ContextForKind<RenderContextKind>,
        runtime: RenderRuntime
    ): ElementContentRenderResult {
        const childReadyPromises: Promise<void>[] = [];
        const childContentResults: Promise<boolean>[] = [];

        const rowNodes = this.settings.rows.map((row, rowIndex) => {
            const rowPath = runtime.getCurrentElementChildListItemPath('rows', rowIndex);
            const rowChildrenPath = appendStructurePath(rowPath, 'children');
            const rendered = renderDefinitionListWithAddElementSlot(
                row.children,
                ctx,
                runtime,
                rowChildrenPath,
                ctx.kind,
                'child-list'
            );
            childReadyPromises.push(rendered.ready);
            if (row.children.length > 0) {
                childContentResults.push(rendered.hasRenderableContent);
            }
            // A flowable child can need more space than the saved row height. Let the row
            // grow so later rows follow it, while keeping that height for its children.
            let rowStyle: React.CSSProperties | undefined;
            if (row.height) {
                rowStyle = runtime.containsFlowableElement(row.children)
                    ? ({
                          minHeight: row.height,
                          '--rb-flowable-container-height': row.height,
                      } as React.CSSProperties)
                    : { height: row.height };
            }

            return (
                <div key={`row-${rowIndex}`} className="flex flex-col gap-2" style={rowStyle}>
                    {rendered.nodes}
                </div>
            );
        });

        return {
            node: <div className="mb-4 flex flex-col gap-4">{rowNodes}</div>,
            ready: Promise.all(childReadyPromises).then(() => undefined),
            hasRenderableContent: combineRenderableContent(childContentResults),
        };
    }
}
